import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DateRangeFilter,
  INSIGHTS_DATE_RANGE_PRESETS,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { FilterBar } from '@/components/shared/filter-bar';
import { SearchInput } from '@/components/shared/search-input';
import { CreateExpenseCategoryDialog } from '@/components/spending/create-expense-category-dialog';
import { Listbox } from '@/components/ui/listbox';
import { api } from '@/lib/api';
import { dataTableMoneyColumnTotals } from '@/lib/money-totals';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { formatNumber } from '@/utils/format';

interface CategorySummaryRow {
  id: string;
  name: string;
  count: number;
  amountMinor: number;
  currency: string;
}

interface ExpenseInsights {
  paidAmountMinor: number;
  paidCount: number;
  currency: string;
  byCategory: Array<{
    categoryId: string;
    categoryName: string;
    amountMinor: number;
    count: number;
  }>;
}

const EMPTY_SHOW = 'show';
const EMPTY_HIDE = 'hide';
type EmptyFilter = typeof EMPTY_SHOW | typeof EMPTY_HIDE;

export interface ExpenseCategoriesSummaryPanelProps {
  createOpen: boolean;
  onCreateOpenChange: (open: boolean) => void;
}

export const ExpenseCategoriesSummaryPanel = ({
  createOpen,
  onCreateOpenChange,
}: ExpenseCategoriesSummaryPanelProps) => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const church = useChurchStore((state) => state.church);
  const timezone = usePreferences((state) => state.timezone);
  const [search, setSearch] = useState('');
  const [emptyFilter, setEmptyFilter] = useState<EmptyFilter>(EMPTY_SHOW);
  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    preset: 'thisMonth',
    ...resolvePreset('thisMonth', timezone),
  }));

  const baseCurrency = church?.baseCurrency ?? 'GHS';

  const categories = useQuery({
    queryKey: ['expenses', 'categories'],
    queryFn: () =>
      api.get<{ data: { id: string; name: string; isActive: boolean }[] }>(
        '/expenses/categories',
      ),
  });

  const insights = useQuery({
    queryKey: ['expenses', 'insights', dateRange.from, dateRange.to],
    queryFn: () =>
      api.get<ExpenseInsights>('/expenses/insights', {
        query: {
          dateFrom: dateRange.from ?? undefined,
          dateTo: dateRange.to ?? undefined,
        },
      }),
  });

  const rows = useMemo((): CategorySummaryRow[] => {
    const totalsByCategory = new Map(
      (insights.data?.byCategory ?? []).map((row) => [row.categoryId, row]),
    );
    const activeCategories = (categories.data?.data ?? []).filter(
      (row) => row.isActive !== false,
    );

    return activeCategories
      .map((category) => {
        const totals = totalsByCategory.get(category.id);
        return {
          id: category.id,
          name: category.name,
          count: totals?.count ?? 0,
          amountMinor: totals?.amountMinor ?? 0,
          currency: insights.data?.currency ?? baseCurrency,
        };
      })
      .sort((left, right) => {
        if (right.amountMinor !== left.amountMinor) {
          return right.amountMinor - left.amountMinor;
        }
        return left.name.localeCompare(right.name);
      });
  }, [baseCurrency, categories.data?.data, insights.data?.byCategory, insights.data?.currency]);

  const filteredRows = useMemo(() => {
    let result = rows;
    if (emptyFilter === EMPTY_HIDE) {
      result = result.filter((row) => row.count > 0 || row.amountMinor > 0);
    }
    const query = search.trim().toLowerCase();
    if (query) {
      result = result.filter((row) => row.name.toLowerCase().includes(query));
    }
    return result;
  }, [emptyFilter, rows, search]);

  const appliedCount = useMemo(() => {
    let count = 0;
    if (search.trim()) count += 1;
    if (emptyFilter === EMPTY_HIDE) count += 1;
    if (dateRange.preset !== 'thisMonth') count += 1;
    return count;
  }, [dateRange.preset, emptyFilter, search]);

  const tableTotals = useMemo(() => {
    const totalAmountMinor = filteredRows.reduce((sum, row) => sum + row.amountMinor, 0);
    const count = filteredRows.reduce((sum, row) => sum + row.count, 0);
    return {
      amountMinor: totalAmountMinor,
      currency: insights.data?.currency ?? baseCurrency,
      count,
    };
  }, [baseCurrency, filteredRows, insights.data?.currency]);

  const columns: DataTableColumn<CategorySummaryRow>[] = [
    {
      id: 'name',
      header: t('categories.columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'count',
      header: t('categories.columns.count'),
      cell: (row) => formatNumber(row.count),
    },
    {
      id: 'total',
      header: t('categories.columns.total'),
      money: (row) => ({ amount: row.amountMinor, currency: row.currency }),
    },
  ];

  return (
    <div className="space-y-3">
      <FilterBar
        appliedCount={appliedCount}
        onClearAll={() => {
          setSearch('');
          setEmptyFilter(EMPTY_SHOW);
          setDateRange({
            preset: 'thisMonth',
            ...resolvePreset('thisMonth', timezone),
          });
        }}
        labels={{
          filters: t('categories.filters.title'),
          clearAll: t('categories.filters.clearAll'),
          applied: (count) => t('categories.filters.applied', { count }),
        }}
        leading={
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t('categories.filters.search')}
            clearLabel={tCommon('search.clear')}
            className="max-w-none sm:w-56"
            aria-label={t('categories.filters.search')}
          />
        }
      >
        {({ popoverPortal }) => (
          <>
            <div className="w-44 min-w-[10rem]">
              <Listbox
                value={emptyFilter}
                onChange={(value) => setEmptyFilter((value as EmptyFilter) ?? EMPTY_SHOW)}
                options={[
                  { value: EMPTY_SHOW, label: t('categories.filters.showEmpty') },
                  { value: EMPTY_HIDE, label: t('categories.filters.hideEmpty') },
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
            <DateRangeFilter
              value={dateRange}
              onChange={setDateRange}
              popoverPortal={popoverPortal}
              labels={{
                today: tCommon('date.today'),
                yesterday: tCommon('date.yesterday'),
                thisWeek: tCommon('date.thisWeek'),
                lastWeek: tCommon('date.lastWeek'),
                thisMonth: tCommon('date.thisMonth'),
                lastMonth: tCommon('date.lastMonth'),
                thisQuarter: tCommon('date.thisQuarter'),
                thisYear: tCommon('date.thisYear'),
                lastYear: tCommon('date.lastYear'),
                allTime: tCommon('date.allTime'),
                custom: tCommon('date.custom'),
                from: t('expenses.insights.dateFrom'),
                to: t('expenses.insights.dateTo'),
              }}
              presets={INSIGHTS_DATE_RANGE_PRESETS}
            />
          </>
        )}
      </FilterBar>

      <DataTable
        rows={filteredRows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={categories.isLoading || insights.isLoading}
        totals={dataTableMoneyColumnTotals('total', tableTotals, baseCurrency)}
        empty={
          <p className="text-body-sm text-fg-secondary">
            {appliedCount > 0 ? t('categories.emptyFiltered') : t('categories.empty')}
          </p>
        }
      />

      <CreateExpenseCategoryDialog
        open={createOpen}
        onOpenChange={onCreateOpenChange}
        onCreated={() => {
          void insights.refetch();
        }}
      />
    </div>
  );
};
